import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ChevronDown, ChevronRight, Loader2, Check, AlertTriangle } from 'lucide-react';
import { fmt, fmtCur } from './helpers';

export function RMRevisionPanel({ open, onClose, defaultCustomer }) {
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState('');
  const [revisionDate, setRevisionDate] = useState('');
  // Label is derived from the picked date (Indian FY quarters, or beginning/mid/end of month).
  const revisionLabel = deriveRevisionLabel(revisionDate);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [gradeEdits, setGradeEdits] = useState({});       // { grade: newRmPrice }
  const [gradeScrapEdits, setGradeScrapEdits] = useState({}); // { grade: newScrapPrice }
  const [spEdits, setSpEdits] = useState({});             // { part_id: newSp }
  const [manualSp, setManualSp] = useState(new Set());    // part_ids the user manually overrode
  const [showSP, setShowSP] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/customers').then(r => setCustomers(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open && defaultCustomer) setCustomer(defaultCustomer);
  }, [open, defaultCustomer]);

  useEffect(() => {
    if (!open) {
      setCustomer(''); setRevisionDate(''); setParts([]);
      setGradeEdits({}); setGradeScrapEdits({}); setSpEdits({}); setManualSp(new Set()); setShowSP(false);
    }
  }, [open]);

  // Suggested selling price using pass-through RM/scrap delta:
  // ΔNet RMC = gross_weight × ΔRM_rate − (gross_weight − net_weight) × Δscrap_rate
  const suggestedSp = useCallback((p) => {
    const g = p.material_grade || 'Unknown';
    const curRm = p.rm_price_per_kg ?? p.rm_price ?? 0;
    const curScrap = p.scrap_price_per_kg ?? 0;
    const newRm = gradeEdits[g] ?? curRm;
    const newScrap = gradeScrapEdits[g] ?? curScrap;
    const gw = p.gross_weight ?? 0;
    const nw = p.net_weight ?? 0;
    const curSp = p.selling_price ?? 0;
    const deltaNetRmc = gw * (newRm - curRm) - (gw - nw) * (newScrap - curScrap);
    return Math.round((curSp + deltaNetRmc) * 100) / 100;
  }, [gradeEdits, gradeScrapEdits]);

  const handleLoadParts = useCallback(async () => {
    if (!customer) { toast.error('Select a customer'); return; }
    if (!revisionDate) { toast.error('Pick a revision date'); return; }
    setLoading(true);
    try {
      const res = await api.get(`/parts?customer=${encodeURIComponent(customer)}&limit=5000`);
      const list = res.data?.parts || res.data || [];
      setParts(list);
      // Pre-fill grade edits with current dominant RM + scrap per grade
      const gradeRm = {}, gradeScrap = {};
      list.forEach(p => {
        const g = p.material_grade || 'Unknown';
        if (!gradeRm[g]) { gradeRm[g] = []; gradeScrap[g] = []; }
        gradeRm[g].push(p.rm_price_per_kg ?? p.rm_price ?? 0);
        gradeScrap[g].push(p.scrap_price_per_kg ?? 0);
      });
      const initRm = {}, initScrap = {};
      Object.keys(gradeRm).forEach(g => {
        initRm[g] = getDominantPrice(gradeRm[g]);
        initScrap[g] = getDominantPrice(gradeScrap[g]);
      });
      setGradeEdits(initRm);
      setGradeScrapEdits(initScrap);
      // Pre-fill SP edits with current selling prices
      const spInit = {};
      list.forEach(p => { spInit[p.part_id] = p.selling_price ?? 0; });
      setSpEdits(spInit);
      setManualSp(new Set());
      setShowSP(false);
    } catch {
      toast.error('Failed to load parts');
    } finally {
      setLoading(false);
    }
  }, [customer, revisionDate]);

  // Auto-derive suggested selling prices when RM / scrap rates change (skip manual overrides)
  useEffect(() => {
    if (parts.length === 0) return;
    setSpEdits(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        if (!manualSp.has(p.part_id)) next[p.part_id] = suggestedSp(p);
      });
      return next;
    });
  }, [gradeEdits, gradeScrapEdits, parts, manualSp, suggestedSp]);

  // Group parts by grade
  const gradeGroups = useMemo(() => {
    const map = {};
    parts.forEach(p => {
      const g = p.material_grade || 'Unknown';
      if (!map[g]) map[g] = [];
      map[g].push(p);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0])).map(([grade, gparts]) => {
      const prices = gparts.map(p => p.rm_price_per_kg ?? p.rm_price ?? 0);
      const scraps = gparts.map(p => p.scrap_price_per_kg ?? 0);
      const dominant = getDominantPrice(prices);
      const dominantScrap = getDominantPrice(scraps);
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      const mixed = Math.abs(max - min) > 0.001;
      return { grade, parts: gparts, count: gparts.length, dominant, dominantScrap, min, max, mixed };
    });
  }, [parts]);

  // Count grades changed (RM or scrap)
  const gradesChanged = useMemo(() => {
    return gradeGroups.filter(g => {
      const newRm = gradeEdits[g.grade];
      const newScrap = gradeScrapEdits[g.grade];
      const rmChanged = newRm !== undefined && (g.mixed || Math.abs(newRm - g.dominant) > 0.001);
      const scrapChanged = newScrap !== undefined && Math.abs(newScrap - g.dominantScrap) > 0.001;
      return rmChanged || scrapChanged;
    });
  }, [gradeGroups, gradeEdits, gradeScrapEdits]);

  // Count SP changed
  const spChangedCount = useMemo(() => {
    return parts.filter(p => {
      const newSp = spEdits[p.part_id];
      const curSp = p.selling_price ?? 0;
      return newSp !== undefined && Math.abs(newSp - curSp) > 0.001;
    }).length;
  }, [parts, spEdits]);

  // Total affected parts (RM changed OR scrap changed OR SP changed)
  const affectedParts = useMemo(() => {
    return parts.filter(p => {
      const g = p.material_grade || 'Unknown';
      const curRm = p.rm_price_per_kg ?? p.rm_price ?? 0;
      const newRm = gradeEdits[g] ?? curRm;
      const rmChanged = newRm > 0 && Math.abs(newRm - curRm) > 0.001;
      const curScrap = p.scrap_price_per_kg ?? 0;
      const newScrap = gradeScrapEdits[g] ?? curScrap;
      const scrapChanged = Math.abs(newScrap - curScrap) > 0.001;
      const curSp = p.selling_price ?? 0;
      const newSp = spEdits[p.part_id] ?? curSp;
      const spChanged = Math.abs(newSp - curSp) > 0.001;
      return rmChanged || scrapChanged || spChanged;
    });
  }, [parts, gradeEdits, gradeScrapEdits, spEdits]);

  const handleApply = useCallback(async () => {
    if (affectedParts.length === 0) { toast.error('No parts have been changed'); return; }
    if (!window.confirm(`Apply revision "${revisionLabel}" to ${affectedParts.length} parts? Current values will be saved to history.`)) return;
    setApplying(true);
    try {
      const payload = {
        revision_label: revisionLabel.trim(),
        parts: affectedParts.map(p => {
          const g = p.material_grade || 'Unknown';
          const curRm = p.rm_price_per_kg ?? p.rm_price ?? 0;
          const curScrap = p.scrap_price_per_kg ?? 0;
          const curSp = p.selling_price ?? 0;
          return {
            part_id: p.part_id,
            new_rm_price: gradeEdits[g] ?? curRm,
            new_scrap_price: gradeScrapEdits[g] ?? curScrap,
            new_selling_price: spEdits[p.part_id] ?? curSp,
          };
        }),
      };
      const res = await api.post('/parts/bulk-revise', payload);
      toast.success(`${res.data.updated} parts updated, ${res.data.skipped} skipped`);
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply revision');
    } finally {
      setApplying(false);
    }
  }, [affectedParts, gradeEdits, gradeScrapEdits, spEdits, revisionLabel, onClose]);

  if (!open) return null;

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="rm-revision-panel">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900 text-lg">RM Rate Revision</DialogTitle>
          <p className="text-xs text-slate-500">Update RM prices by grade and selling prices per part. Current values are saved to costing history.</p>
        </DialogHeader>

        {/* Header inputs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mt-3" data-testid="revision-header">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Customer</label>
            <div className="relative">
              <select data-testid="rev-customer-select" value={customer}
                onChange={e => { setCustomer(e.target.value); setParts([]); setGradeEdits({}); setGradeScrapEdits({}); setSpEdits({}); setManualSp(new Set()); }}
                className="w-full appearance-none bg-slate-50 border border-slate-300 rounded-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 pr-8">
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c.customer_id || c.name} value={c.name}>{c.name}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Revision Date</label>
            <input data-testid="rev-date-input" type="date" value={revisionDate}
              onChange={e => setRevisionDate(e.target.value)}
              className="w-full bg-slate-50 border border-slate-300 rounded-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500" />
            <p className="text-[11px] mt-1 h-4 leading-4" data-testid="rev-label-derived">
              {revisionLabel
                ? <>Label: <span className="font-bold text-slate-700">{revisionLabel}</span></>
                : <span className="text-slate-400">Pick a date — label is auto-set</span>}
            </p>
          </div>
          <button data-testid="load-parts-btn" onClick={handleLoadParts}
            disabled={!customer || !revisionLabel.trim() || loading}
            className="flex items-center justify-center gap-2 px-5 py-2 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            Load Parts
          </button>
          <div className="text-right">
            {parts.length > 0 && (
              <span className="text-xs text-slate-500">{parts.length} parts across <span className="font-bold text-slate-700">{gradeGroups.length} grades</span></span>
            )}
          </div>
        </div>

        {/* Step 1: Grade-level RM table */}
        {parts.length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-sm overflow-hidden" data-testid="grade-table-wrapper">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">RM &amp; Scrap Rate by Grade</p>
            </div>
            <div className="overflow-x-auto max-h-[35vh] overflow-y-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-100 border-b border-slate-200">
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600 min-w-[160px]">RM Grade</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-slate-600 min-w-[60px]">Parts</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 min-w-[120px]">Cur RM/kg</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-orange-600 min-w-[120px]">New RM/kg</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 min-w-[110px]">Cur Scrap/kg</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-orange-600 min-w-[120px]">New Scrap/kg</th>
                  </tr>
                </thead>
                <tbody>
                  {gradeGroups.map(g => {
                    const newRm = gradeEdits[g.grade] ?? g.dominant;
                    const newScrap = gradeScrapEdits[g.grade] ?? g.dominantScrap;
                    const rmChanged = g.mixed || Math.abs(newRm - g.dominant) > 0.001;
                    const scrapChanged = Math.abs(newScrap - g.dominantScrap) > 0.001;
                    const changed = rmChanged || scrapChanged;
                    return (
                      <tr key={g.grade}
                        className={`border-b border-slate-100 transition-colors ${changed ? 'bg-amber-50/60' : 'hover:bg-slate-50'}`}
                        data-testid={`grade-row-${g.grade}`}>
                        <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-800">{g.grade}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex px-2 py-0.5 bg-slate-100 rounded-full text-[11px] font-bold text-slate-600">{g.count}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {g.mixed ? (
                            <span className="flex flex-col items-end gap-0.5">
                              <span className="flex items-center gap-1">
                                <AlertTriangle size={12} className="text-amber-500" />
                                <span className="font-mono text-xs text-amber-700">{fmtCur(g.min)} – {fmtCur(g.max)}</span>
                              </span>
                              <span className="text-[10px] text-amber-500">All set to new value</span>
                            </span>
                          ) : (
                            <span className="font-mono text-xs text-slate-500">{fmtCur(g.dominant)}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" step="0.01" value={newRm}
                            onChange={ev => {
                              const val = parseFloat(ev.target.value);
                              setGradeEdits(prev => ({ ...prev, [g.grade]: isNaN(val) ? g.dominant : val }));
                            }}
                            data-testid={`new-grade-rm-${g.grade}`}
                            className={`w-[110px] text-right font-mono text-xs px-2 py-1.5 border rounded-sm focus:outline-none focus:ring-1 focus:ring-orange-400 ${
                              rmChanged ? 'border-amber-400 bg-amber-50 font-bold text-amber-800' : 'border-slate-200 bg-white text-slate-700'
                            }`} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="font-mono text-xs text-slate-500">{fmtCur(g.dominantScrap)}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" step="0.01" value={newScrap}
                            onChange={ev => {
                              const val = parseFloat(ev.target.value);
                              setGradeScrapEdits(prev => ({ ...prev, [g.grade]: isNaN(val) ? g.dominantScrap : val }));
                            }}
                            data-testid={`new-grade-scrap-${g.grade}`}
                            className={`w-[110px] text-right font-mono text-xs px-2 py-1.5 border rounded-sm focus:outline-none focus:ring-1 focus:ring-orange-400 ${
                              scrapChanged ? 'border-amber-400 bg-amber-50 font-bold text-amber-800' : 'border-slate-200 bg-white text-slate-700'
                            }`} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 2: Collapsible Selling Price table */}
        {parts.length > 0 && (
          <div className="mt-3 border border-slate-200 rounded-sm overflow-hidden" data-testid="sp-section">
            <button onClick={() => setShowSP(!showSP)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              data-testid="sp-toggle-btn">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                {showSP ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Review Selling Prices (auto-derived)
              </span>
              {spChangedCount > 0 && (
                <span className="text-xs font-bold text-amber-600">{spChangedCount} changed</span>
              )}
            </button>
            {showSP && (
              <div className="overflow-x-auto max-h-[35vh] overflow-y-auto border-t border-slate-200">
                <p className="text-[10px] text-slate-400 px-3 py-1.5 bg-slate-50/60 border-b border-slate-100">
                  Suggested prices auto-update from RM/scrap changes. Edit any value to override (override is kept).
                </p>
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-100 border-b border-slate-200">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 min-w-[140px]">Part No.</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 min-w-[160px]">Part Name</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 min-w-[90px]">Grade</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 min-w-[80px]">Cur Sell</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-blue-600 min-w-[90px]">Suggested</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-orange-600 min-w-[110px]">New Sell</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.map(p => {
                      const curSp = p.selling_price ?? 0;
                      const newSp = spEdits[p.part_id] ?? curSp;
                      const suggested = suggestedSp(p);
                      const isManual = manualSp.has(p.part_id);
                      const changed = Math.abs(newSp - curSp) > 0.001;
                      return (
                        <tr key={p.part_id}
                          className={`border-b border-slate-100 transition-colors ${changed ? 'bg-amber-50/60' : 'hover:bg-slate-50'}`}
                          data-testid={`sp-row-${p.part_id}`}>
                          <td className="px-3 py-1.5 font-mono text-xs font-semibold text-slate-800">{p.part_number}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-600 truncate max-w-[200px]">{p.part_name}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-400">{p.material_grade || '—'}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-400">{fmtCur(curSp)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs text-blue-600" data-testid={`suggested-sp-${p.part_id}`}>{fmtCur(suggested)}</td>
                          <td className="px-3 py-1.5 text-right">
                            <input type="number" step="0.01" value={newSp}
                              onChange={ev => {
                                const val = parseFloat(ev.target.value) || 0;
                                setManualSp(prev => new Set(prev).add(p.part_id));
                                setSpEdits(prev => ({ ...prev, [p.part_id]: val }));
                              }}
                              data-testid={`new-sp-${p.part_id}`}
                              className={`w-[100px] text-right font-mono text-xs px-2 py-1 border rounded-sm focus:outline-none focus:ring-1 focus:ring-orange-400 ${
                                changed ? 'border-amber-400 bg-amber-50 font-bold text-amber-800' : 'border-slate-200 bg-white text-slate-700'
                              }`} />
                            {isManual && <span className="block text-[9px] text-slate-400 mt-0.5">manual</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Preview summary + Apply */}
        {parts.length > 0 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              {affectedParts.length > 0 ? (
                <span>
                  Applying to <span className="font-bold text-amber-700">{affectedParts.length}</span> part{affectedParts.length !== 1 ? 's' : ''} —{' '}
                  {gradesChanged.length > 0 && (
                    <span><span className="font-bold text-slate-700">{gradesChanged.length}</span> grade{gradesChanged.length !== 1 ? 's' : ''} RM/scrap updated</span>
                  )}
                  {gradesChanged.length > 0 && spChangedCount > 0 && <span className="mx-1 text-slate-300">|</span>}
                  {spChangedCount > 0 && (
                    <span><span className="font-bold text-slate-700">{spChangedCount}</span> selling price{spChangedCount !== 1 ? 's' : ''} changed</span>
                  )}
                </span>
              ) : (
                <span className="text-slate-400">No changes yet</span>
              )}
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => onClose(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-sm transition-colors">
                Cancel
              </button>
              <button data-testid="apply-revision-btn" onClick={handleApply}
                disabled={affectedParts.length === 0 || applying}
                className="flex items-center gap-2 px-5 py-2 bg-orange-500 text-white text-sm font-bold uppercase rounded-sm hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {applying ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Apply Revision ({affectedParts.length} parts)
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
// Month that starts each Indian-FY quarter -> quarter number (FY runs Apr–Mar).
const QUARTER_START_MONTH = { 4: 1, 7: 2, 10: 3, 1: 4 };

/**
 * Derive a revision label from a yyyy-mm-dd date:
 *  - 1st of Apr/Jul/Oct/Jan  -> Indian FY quarter, e.g. "Q1 FY26-27"
 *  - any other date          -> "Beginning/Mid/End <Month> <Year>" by day-of-month
 */
function deriveRevisionLabel(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  if (d === 1 && QUARTER_START_MONTH[m]) {
    const q = QUARTER_START_MONTH[m];
    const fyStart = m >= 4 ? y : y - 1;               // Jan–Mar belong to the FY that began the previous April
    const yy = (n) => String(n % 100).padStart(2, '0');
    return `Q${q} FY${yy(fyStart)}-${yy(fyStart + 1)}`;
  }
  const part = d <= 10 ? 'Beginning' : d <= 20 ? 'Mid' : 'End';
  return `${part} ${MONTH_NAMES[m - 1]} ${y}`;
}

/** Return the most common value in an array of numbers. */
function getDominantPrice(prices) {
  const freq = {};
  prices.forEach(p => {
    const key = p.toFixed(4);
    freq[key] = (freq[key] || 0) + 1;
  });
  let best = prices[0] || 0, bestCount = 0;
  Object.entries(freq).forEach(([k, c]) => {
    if (c > bestCount) { bestCount = c; best = parseFloat(k); }
  });
  return best;
}
